import axiosInstance from './axiosConfig';
import { authService } from './authService';

interface ActivityConfig {
  idleTimeout: number;
  pingInterval: number;
  minActivityInterval: number;
}

const DEFAULT_CONFIG: ActivityConfig = {
  idleTimeout: 15 * 60 * 1000,    // 15 minutes
  pingInterval: 5 * 60 * 1000,    // 5 minutes
  minActivityInterval: 60 * 1000  // 1 minute
};

// Debounce function to limit how often a function can be called
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}

class ActivityTracker {
  private pingInterval: NodeJS.Timeout | null = null;
  private lastActivity: number = Date.now();
  private lastPingTime: number = 0;
  private isTracking: boolean = false;
  private config: ActivityConfig;
  private visibilityChangeHandler: (() => void) | null = null;

  // Optimized events to track
  private readonly trackedEvents = [
    'mousedown',    // More efficient than mousemove
    'keydown',      // More efficient than keypress
    'click',        // User interaction
    'touchstart',   // Mobile interaction
    'focus'         // Tab switching
  ];

  constructor(config: Partial<ActivityConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.setupEventListeners();
    this.setupVisibilityListener();
  }

  private setupEventListeners(): void {
    this.trackedEvents.forEach(event => {
      window.addEventListener(event, this.handleUserActivity);
    });
  }

  private setupVisibilityListener(): void {
    // Use Page Visibility API to track tab visibility
    // This is critical for call centers - agents need to stay logged in even when tab is hidden
    this.visibilityChangeHandler = () => {
      const isVisible = !document.hidden;
      console.log(`ActivityTracker: Tab visibility changed - visible: ${isVisible}`);
      
      // When tab becomes visible, update last activity to prevent immediate expiration
      if (isVisible) {
        this.lastActivity = Date.now();
      }
      // When tab is hidden, we still keep pinging (important for call center - agent is still "at desk")
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private isTabVisible(): boolean {
    return !document.hidden;
  }

  // Debounced activity handler
  private handleUserActivity = debounce(() => {
    this.lastActivity = Date.now();
  }, 1000); // Only update once per second

  private async pingServer(): Promise<void> {
    try {
      const now = Date.now();
      
      // Skip if last ping was too recent
      if (now - this.lastPingTime < this.config.minActivityInterval) {
        return;
      }
      
      // Check if user is still authenticated before pinging
      const token = authService.getToken();
      if (!token || !authService.isAuthenticated()) {
        console.log('ActivityTracker: User not authenticated, stopping tracking. Token exists:', !!token);
        this.stopTracking();
        return;
      }
      
      console.log('ActivityTracker: Pinging server');
      await axiosInstance.post('/auth/ping');
      this.lastPingTime = now;
      console.log('ActivityTracker: Ping successful');
    } catch (error: any) {
      console.error('ActivityTracker: Error pinging server:', error);
      
      // If it's an authentication error, stop tracking
      if (error.response?.status === 401) {
        console.log('ActivityTracker: Authentication failed, stopping tracking');
        this.stopTracking();
      }
    }
  }

  public startTracking(): void {
    if (this.isTracking) {
      console.log('ActivityTracker: Already tracking, skipping start');
      return;
    }

    // Only start tracking if user is authenticated and has a valid token
    const token = authService.getToken();
    if (!token || !authService.isAuthenticated()) {
      console.log('ActivityTracker: User not authenticated, not starting tracking. Token exists:', !!token);
      return;
    }

    console.log('ActivityTracker: Starting tracking with token:', token ? 'exists' : 'null');
    
    this.isTracking = true;
    this.lastActivity = Date.now();
    this.lastPingTime = 0;

    console.log('ActivityTracker: Started tracking user activity');

    // Do an initial ping after a short delay to ensure token is ready
    setTimeout(async () => {
      if (this.isTracking && authService.isAuthenticated()) {
        console.log('ActivityTracker: Doing initial ping');
        await this.pingServer();
      }
    }, 1000); // Reduced to 1 second delay for initial ping

    // Start pinging server at regular intervals
    this.pingInterval = setInterval(async () => {
      // Stop tracking if user is no longer authenticated
      const token = authService.getToken();
      if (!token || !authService.isAuthenticated()) {
        console.log('ActivityTracker: User no longer authenticated, stopping tracking. Token exists:', !!token);
        this.stopTracking();
        return;
      }

      // CALL CENTER BEHAVIOR: Keep pinging as long as tab is open (visible OR hidden)
      // 
      // Why this is important for call centers:
      // - Agents may wait long periods (hours) between calls
      // - They need to stay logged in to receive calls even when not actively clicking
      // - Tab being open = agent is "at desk" and available
      // - Tab being hidden = agent might be on another tab but still available
      // 
      // How it works:
      // - Each ping updates last_activity timestamp on backend
      // - Backend checks: if last_activity < 15 minutes ago → session expires
      // - As long as we ping every 5 minutes, session stays alive
      // - If tab is closed, JavaScript stops → no more pings → session expires after 15 min
      //
      // This allows agents to work an 8-hour shift with minimal interaction
      await this.pingServer();
    }, this.config.pingInterval);
  }

  public stopTracking(): void {
    if (!this.isTracking) return;

    this.isTracking = false;
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  public cleanup(): void {
    this.stopTracking();
    this.trackedEvents.forEach(event => {
      window.removeEventListener(event, this.handleUserActivity);
    });
    // Remove visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
  }

  // Method to update configuration
  public updateConfig(newConfig: Partial<ActivityConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart tracking with new config if currently tracking
    if (this.isTracking) {
      this.stopTracking();
      this.startTracking();
    }
  }

  // Method to restart tracking (useful when user logs in)
  public restartTracking(): void {
    console.log('ActivityTracker: Restarting tracking');
    this.stopTracking();
    
    // Add a small delay to ensure token is properly set after login
    setTimeout(() => {
      // Double-check authentication before starting
      const token = authService.getToken();
      if (token && authService.isAuthenticated()) {
        console.log('ActivityTracker: Token confirmed, starting tracking');
        this.startTracking();
      } else {
        console.log('ActivityTracker: User not authenticated after delay, not starting tracking. Token exists:', !!token);
      }
    }, 500); // Reduced to 500ms delay
  }
}

export const activityTracker = new ActivityTracker(); 